import React, { useState, useEffect, useMemo } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import './BoardGantt.css';

const BoardGantt = ({ board, token, onUpdate }) => {
  const [viewMode, setViewMode] = useState(ViewMode.Day);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (board && board.groups) {
      const ganttTasks = convertToGanttFormat(board.groups);
      setTasks(ganttTasks);
    }
  }, [board]);

  const convertToGanttFormat = (groups) => {
    const ganttTasks = [];
    
    groups.forEach((group, groupIndex) => {
      // Add group as a project task
      const groupStartDates = group.items
        .filter(item => item.startDate)
        .map(item => new Date(item.startDate));
      const groupEndDates = group.items
        .filter(item => item.dueDate)
        .map(item => new Date(item.dueDate));
      
      const groupStart = groupStartDates.length > 0
        ? new Date(Math.min(...groupStartDates))
        : new Date();
      const groupEnd = groupEndDates.length > 0
        ? new Date(Math.max(...groupEndDates))
        : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days from now
      
      ganttTasks.push({
        id: group.id,
        name: group.name,
        start: groupStart,
        end: groupEnd,
        progress: calculateGroupProgress(group.items),
        type: 'project',
        hideChildren: false,
        styles: {
          backgroundColor: group.color || '#0F0F10',
          backgroundSelectedColor: group.color || '#0F0F10',
          progressColor: '#ffffff',
          progressSelectedColor: '#ffffff'
        }
      });
      
      // Add group items as tasks
      group.items.forEach((item) => {
        const start = item.startDate ? new Date(item.startDate) : new Date();
        let end = item.dueDate ? new Date(item.dueDate) : new Date(start.getTime() + 3 * 24 * 60 * 60 * 1000); // 3 days default
        
        // Ensure end is after start
        if (end <= start) {
          end = new Date(start.getTime() + 24 * 60 * 60 * 1000); // 1 day minimum
        }
        
        const taskColor = getStatusColor(item.status);
        
        ganttTasks.push({
          id: item.id,
          name: item.name,
          start: start,
          end: end,
          progress: item.progress || 0,
          type: 'task',
          project: group.id,
          dependencies: item.dependencies || [],
          styles: {
            backgroundColor: taskColor,
            backgroundSelectedColor: taskColor,
            progressColor: '#ffffff',
            progressSelectedColor: '#ffffff'
          },
          // Store original data for updates
          originalData: item
        });
      });
    });
    
    return ganttTasks;
  };

  const calculateGroupProgress = (items) => {
    if (!items || items.length === 0) return 0;
    const totalProgress = items.reduce((sum, item) => sum + (item.progress || 0), 0);
    return Math.round(totalProgress / items.length);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': '#B9B7AE',
      'In Progress': '#0F0F10',
      'Done': '#1DBF73',
      'Blocked': '#E24B5B'
    };
    return colors[status] || '#0F0F10';
  };

  const handleTaskChange = async (task) => {
    try {
      // Update task dates in backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${task.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startDate: task.start.toISOString(),
          dueDate: task.end.toISOString()
        })
      });
      
      if (response.ok) {
        onUpdate(); // Refresh board data
      }
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleProgressChange = async (task) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${task.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          progress: Math.round(task.progress)
        })
      });
      
      if (response.ok) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const handleExpanderClick = (task) => {
    // Handle project expansion/collapse
    const updatedTasks = tasks.map(t => {
      if (t.id === task.id) {
        return { ...t, hideChildren: !t.hideChildren };
      }
      return t;
    });
    setTasks(updatedTasks);
  };

  if (!tasks || tasks.length === 0) {
    return (
      <div className="gantt-empty-state">
        <h3>No Tasks with Dates</h3>
        <p>Add start and due dates to tasks to see them in Gantt view</p>
      </div>
    );
  }

  return (
    <div className="board-gantt">
      <div className="gantt-controls">
        <div className="gantt-view-switcher">
          <button
            className={`gantt-view-btn ${viewMode === ViewMode.Day ? 'active' : ''}`}
            onClick={() => setViewMode(ViewMode.Day)}
            data-testid="gantt-day-view"
          >
            Day
          </button>
          <button
            className={`gantt-view-btn ${viewMode === ViewMode.Week ? 'active' : ''}`}
            onClick={() => setViewMode(ViewMode.Week)}
            data-testid="gantt-week-view"
          >
            Week
          </button>
          <button
            className={`gantt-view-btn ${viewMode === ViewMode.Month ? 'active' : ''}`}
            onClick={() => setViewMode(ViewMode.Month)}
            data-testid="gantt-month-view"
          >
            Month
          </button>
        </div>
      </div>
      
      <div className="gantt-container">
        <Gantt
          tasks={tasks}
          viewMode={viewMode}
          onDateChange={handleTaskChange}
          onProgressChange={handleProgressChange}
          onExpanderClick={handleExpanderClick}
          listCellWidth="220px"
          columnWidth={viewMode === ViewMode.Month ? 100 : viewMode === ViewMode.Week ? 100 : 60}
          ganttHeight={600}
          barBackgroundColor="#0F0F10"
          barBackgroundSelectedColor="#1A1A1C"
          barProgressColor="#ffffff"
          barProgressSelectedColor="#ffffff"
          projectBackgroundColor="#4F8DFF"
          projectBackgroundSelectedColor="#4a7bc8"
          projectProgressColor="#ffffff"
          projectProgressSelectedColor="#ffffff"
          arrowColor="#0F0F10"
          arrowIndent={20}
          fontSize="14px"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
          todayColor="rgba(0, 115, 234, 0.15)"
          rowHeight={44}
          headerHeight={50}
        />
      </div>
    </div>
  );
};

export default BoardGantt;